import { PlaidItem } from '@/reducers/finances/plaidItemReducer'
import { selectVisibleAccounts } from '@/selectors/financeSelectors'
import { getIsRequiredStepsOnboarding } from '@/selectors/user.selectors'
import { deletePlaidItem } from '@/actions/financialAccountActions'
import { useReselector } from '@/utils/sharedHooks'
import { useAppDispatch } from '@/utils/typeHelpers'

import { Separator, Text, Button } from '@/ui'
import { InstitutionLogo } from './logo'
import { TypographyStyles } from '@/styles'
import { PlaidLink } from '@/components/plaid/PlaidLink'
import { getPlaidButtonLabel } from '@/features/user/settings/helpers'
import { OnboardingTaskIdents } from '@/features/onboarding/config'
import { getUserActionItemByActionItemIdentifier } from '@/features/user/dashboard/user-action-items/lib/userActionItems.selectors'
import { updateUserActionItem } from '@/features/user/dashboard/user-action-items/lib/userActionItems.slice'

export const InstitutionCard = ({
  institution,
  hasOtherAccounts,
}: {
  institution: PlaidItem
  hasOtherAccounts: boolean
}) => {
  const { disconnectedOn, institutionName, accounts } = institution
  const dispatch = useAppDispatch()

  const visibleAccounts = useReselector(
    selectVisibleAccounts,
    institution.id,
    false
  )

  const isRequiredStepsOnboarding = useReselector(getIsRequiredStepsOnboarding)
  const canRemove = isRequiredStepsOnboarding

  const connectBusinessOnboardingItem = useReselector(
    getUserActionItemByActionItemIdentifier,
    OnboardingTaskIdents.CONNECT_A_BUSINESS_BANK_ACCOUNT
  )

  const handleRemoveConnection = async () => {
    // Adding an account auto completes the connect onboarding item but we need to uncomplete it if all accounts removed
    if (!hasOtherAccounts) {
      if (connectBusinessOnboardingItem?.completedAt) {
        await dispatch(
          updateUserActionItem(connectBusinessOnboardingItem.id, {
            id: connectBusinessOnboardingItem.id,
            completedAt: null,
          })
        )
      }
    }
    await dispatch(deletePlaidItem(institution.id))
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex gap-4">
        <div>
          <InstitutionLogo institution={institution} />
        </div>
        <div className="flex flex-col flex-1 gap-3">
          <div className="flex flex-row justify-between items-center">
            <div className="flex flex-col">
              <Text variant={TypographyStyles.H3}>{institutionName}</Text>

              {disconnectedOn && (
                <Text variant={TypographyStyles.BODY_SM}>
                  We have lost connection to this institution. Please reconnect
                  it now.
                </Text>
              )}
            </div>

            {canRemove ? (
              <Button variant="link" onClick={handleRemoveConnection}>
                Remove
              </Button>
            ) : (
              <PlaidLink
                buttonVariant="link"
                style={{ fontSize: '14px' }}
                reconnectButtonText={getPlaidButtonLabel(
                  institution,
                  disconnectedOn,
                  false
                )}
                account={accounts[0]}
                disconnected={Boolean(disconnectedOn)}
              />
            )}
          </div>
          {visibleAccounts.length > 0 && (
            <>
              {visibleAccounts.map((account) => (
                <div
                  key={account.id}
                  className="flex flex-row justify-between items-center"
                >
                  <Text variant={TypographyStyles.BODY_SM}>
                    {account.name} {account.mask}
                  </Text>
                </div>
              ))}
            </>
          )}
        </div>
      </div>
      <Separator className="mb-6" />
    </div>
  )
}
